import Commons from "oc-commons/Commons";
import Arrangement from "./Arrangement";
import Deckel from "./Deckel";
import Rueckwand from "./Rueckwand";
import Element from "./Element";
import Sockel from "./Sockel";
import Schrankboden from "./Schrankboden";
import Brett from "./Brett";
import { v3 } from "oc-threejs/short3";
import Slopings from "./Slopings";
import Maths3 from "oc-threejs/Maths3";
import SchrankO3 from "../object3d/Schrank";
import FullHeightDragable from "./FullHeightDragable";
import Helper from "../model/Helper";
import type { HasLocalCoordinates } from "./HasLocalCoordinates";
import Schwebetuer from "./Schwebetuer";
import Shojituer from "./Shojituer";
import Maths from "oc-commons/Maths";

/**
 * Alle Koordinaten des Schraenke sind lokale Koordinaten. Der Nullpunkt ist links unten hinten,
 * der Punkt rechts oben vorne entspricht der Size des Schraenke.
 */
export default class Schrank implements HasLocalCoordinates {

    // persistent (UserLayer) ------------------------------------------

    angleSide: string = "none";
    direction: string;
    size = v3();

    // sizeTop = x: Breite Oben, y: Kniestock, z: Tiefe oben
    sizeTop = v3();

    artikelnummer = 'Schrank';
    elements = [];
    location = {
        name: "mitte",
    };

    // transient (CompiledLayer) und cached -----------------------------

    arrangement: Arrangement;
    product: ?Object;
    object3d: ?THREE.Object3D;
    schraenke3d: ?THREE.Object3D;
    innerWalls = [];
    outerWalls = {
        left: null,
        right: null,
    };
    deckel: Deckel;
    rueckwand: ?Rueckwand;
    schrankboden: Schrankboden;
    sockel: ?Sockel;
    schwebetuer: ?Schwebetuer;

    // eine Liste von Intervallen bezogen auf die Verdeckung der Öffnungsseite. Beispiel: [[0,80]] bedeutet
    // dass die Öffnungsseite des Schrankes verdeckt ist, und zwar von der linken vorderen Seite aus gezählt.
    // [0,80] meint also eine Verdeckung von insgesamt 80 cm der linken Seite.
    verdeckungsIntervale = [];

    // position, quaternion und matrix sind im World-Koordinatensystem!
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
    _matrix: THREE.Matrix4;
    _inverseMatrix: THREE.Matrix4;
    _matrixNeedsUpdate = true;

    // transient (CompiledLayer) und resetted vor Compilierung ----------

    slopings: Slopings;
    slopeX = 0;

    // die Boxen sind im lokalen Koordinatensystem des Schranks
    box: ?THREE.Box3;
    elementsBox: ?THREE.Box3;
    corpusBox: ?THREE.Box3;
    corpusBoxWithFeet: ?THREE.Box3;
    sizeZisInnenmass = false;

    hasFronts = false;
    hasInterieurDoors = false;

    constructor(arrangement) {
        this.setArrangement(arrangement);
        this.initCoordinateSystem();
        this.schrankboden = new Schrankboden(this);
        this.deckel = new Deckel(this);
        this.rueckwand = new Rueckwand(this);
        this.schwebetuer = new Schwebetuer(this);
        this.shojituer = new Shojituer(this);
        this.outerWalls.left = new Brett(this, 'Aussenwand', 'yz');
        this.outerWalls.right = new Brett(this, 'Aussenwand', 'yz');
    }

    hasRueckwand() {
        return APP.settings.getBool('rueckwand');
    }

    setArrangement(arrangement) {
        this.arrangement = arrangement;
    }

    get parent() {
        return this.arrangement;
    }

    toJSON() {
        return {
            angleSide: this.angleSide,
            direction: this.direction,
            size: this.size,
            sizeTop: this.sizeTop,
            sizeOuter: this.sizeOuter,
            artikelnummer: this.artikelnummer,
            InternerName: this.product.InternerName,
            elements: this.elements.map(e => e.toJSON()),
            boden: this.schrankboden.toJSON(),
            deckel: this.deckel.boeden.map(b => b.toJSON()),
            // fuesse: this.fuesse.map(f => f.toJSON()), scheinen 3D-Objekte zu sein
            innenwaende: this.innerWalls.map(w => w.toJSON()),
            aussenwaende: Object.keys(this.outerWalls).map(key => this.outerWalls[key].toJSON()),
            rueckwaende: this.rueckwand.walls.map(w => w.toJSON()),
            sockel: this.sockel ? this.sockel.toJSON() : null,
            location: _.cloneDeep(this.location),
            schwebetuer: this.arrangement.isSchwebetuer() ? this.schwebetuer.toJSON() : null,
            shojituer: this.arrangement.isShojituer() ? this.shojituer.toJSON() : null,
            schenkel: this.schenkel,
            isVerdeckt: this.isVerdeckt
        };
    }

    fromJSON(json) {
        this.angleSide = json.angleSide || 'none';
        this.direction = json.direction;
        this.size = v3().fromJSON(json.size||{x: 240, y: 0, z: 0});
        this.sizeTop = v3().fromJSON(json.sizeTop || { x: 0, y: 0, z: 0 });
        this.elements = Commons.mapreuse(json.elements, this.elements, e => new Element(this), (d, s) => d.fromJSON(s));
        this.artikelnummer = json.artikelnummer || 'Schrank';
        this.location.bezug = _.get(json, 'location.bezug', 'raum');
        this.location.schrankIndex = _.get(json, 'location.schrankIndex', 0);
        this.location.drehung = _.get(json, 'location.drehung', 0);
        this.location.eckeSelbst = _.get(json, 'location.eckeSelbst', v3(0.5, 0, 0));
        this.location.eckeBezug = _.get(json, 'location.eckeBezug', v3(0.5, 0, 0));
        const jsonSchwebetuer = _.get(json, 'schwebetuer');
        if (this.schwebetuer && jsonSchwebetuer) {
            this.schwebetuer.fromJSON(jsonSchwebetuer);
        }
        const jsonShojituer = _.get(json,'shojituer');
        if (this.shojituer && jsonShojituer) {
            this.shojituer.fromJSON(jsonShojituer);
        }
        this.heal();
        return this;
    }

    heal() {
        if (Commons.isEmpty(this.elements)) {
            this.setNumberOfElements(3);
        }
    }

    copy(schrank, createInterceptor) {
        this.angleSide = schrank.angleSide;
        this.direction = schrank.direction;
        this.size.copy(schrank.size);
        this.sizeTop.copy(schrank.sizeTop);
        this.elements = Commons.mapreuse(schrank.elements, this.elements, () => new Element(this), (d, s) => d.copy(s, createInterceptor));
        // location brauchen wir bei Drag&Drop nicht...
        return this;
    }

    compileBox() {
        this.product = APP.findProduct(this.artikelnummer);
        this._checkSizes();
        this._compileElementWidthAndNumber();
        this._compileSchrank();
        this._compileElementeBoxes();
        if (this.arrangement.isSchwebetuer()) {
            this.schwebetuer.compile();
        }
        if (this.arrangement.isShojituer()) {
            this.shojituer.compile();
        }
    }

    compileInnen() {
        if (APP.settings.getBool('sockel') && APP.settings.getMinValue('sockel_dicke') > 0) {
            if (!this.sockel) {
                this.sockel = new Sockel(this);
            }
            this.sockel.compile();
        } else {
            this.sockel = null;
        }
        this.schrankboden.compile();
        this.deckel.compile();
        this._compileAussenwaende();
        this._compileInnenwaende();
        if (this.hasRueckwand()) {
            this.rueckwand.compile();
        }
        this.elements.forEach(element => element.compileParts());
        this._compileFrontCounts();
        // this._compileMaterialCounts();
        this.elements.forEach(element => element.compileFronts());
        this._compileFuesse();

        this.compiled = true;
    }

    _compileSchrank() {
        APP.model.lazyCreateObject3d(this, () => {
            const schrank = new SchrankO3(this.product, this);
            this.arrangement.object3d.schraenke.add(schrank);
            return schrank;
        });

        const fussHoehe = APP.settings.getBool('puck_fuesse_anzeigen') ? APP.settings.getFloat('puck_fuesse_hoehe') : 0;
        const bottomHeight = APP.settings.getMinValue('sockel_dicke') + APP.settings.getFloat('boden_staerke'); //CHE: sockel_dicke ist bei allen Modellen gesetzt

        // die Box entspricht der BoundingBox des ganzen Schrankes
        this.box = new THREE.Box3(
            v3(0, 0, 0),
            this.size.clone(),
        );

        this.box.max.y = this.elements
            .filter(element => element.locked.y)
            .reduce((max, element) =>
                Math.max(max, this.box.min.y + fussHoehe + bottomHeight + element.size.y + APP.settings.getFloat('deckel_staerke')),
                this.box.max.y);

        // die CorpusBoxWithFeet ist nur ohne Türen
        this.corpusBoxWithFeet = this.box.clone();

        if (this.sizeZisInnenmass) {
            this.box.max.z += this._getFrontOffset();
        } else {
            this.corpusBoxWithFeet.max.z -= this._getFrontOffset();
        }

        // die CorpusBox entspricht dem Schrank ohne Füsse / Türen
        this.corpusBox = this.corpusBoxWithFeet.clone();
        this.corpusBox.min.y += fussHoehe;

        // die ElementsBox entspricht dem Union der Innenmass-Boxen aller Elemente.
        this.elementsBox = this.corpusBox.clone();
        this.elementsBox.min.x += APP.settings.getFloat('aussenwand_staerke');
        this.elementsBox.max.x -= APP.settings.getFloat('aussenwand_staerke');
        this.elementsBox.min.y += bottomHeight;
        this.elementsBox.max.y -= APP.settings.getFloat('deckel_staerke') + APP.settings.getFloat('deckel_versatz_nach_unten');
        this.elementsBox.min.z += this.rueckwandInnerZOffset;

        this.slopeX = this.angleSide === 'left' ? this.size.x - this.sizeTop.x : this.sizeTop.x;

        this.hasFronts = this.elements.some(element => element.getDoors().length > 0);
        this.hasInterieurDoors = this.elements.some(element => element.interieurs.some(interieur => interieur.hasFronts));

        this.slopings = new Slopings(this.size.y, this.elementsBox.min.z);
        if (this.angleSide === 'left') {
            this.slopings.sx = new Slopings.Sloping(0, this.sizeTop.y, this.slopeX, this.size.y);
        } else if (this.angleSide === 'right') {
            this.slopings.sx = new Slopings.Sloping(this.slopeX, this.size.y, this.size.x, this.sizeTop.y);
        } else if (this.angleSide === 'back') {
            this.slopings.sz = new Slopings.Sloping(0, this.sizeTop.y, this.size.z - this.sizeTop.z, this.size.y);
        }

        // if (this.elements.every(e=>e.locked.y)) {
        //     let minY = 0;
        //     this.elements.forEach(e => minY = Math.max(minY, e.size.y));
        //     this.box.max.y = this.box.min.y + fussHoehe + bottomHeight + minY + APP.settings.getFloat('deckel_staerke');
        // }
    }

    _compileElementeBoxes() {
        let elementXMin = APP.settings.getFloat('aussenwand_staerke');
        const brett_staerke = APP.settings.getFloat('brett_staerke');
        this.elements.forEach((element, index) => {
            element.compileBox(elementXMin);
            elementXMin += element.size.x + brett_staerke;
        });
    }

    _compileAussenwaende() {
        const walls = this.outerWalls;
        this._compileAussenwand('left', walls.left);
        this._compileAussenwand('right', walls.right);
        this._wandGrater(walls.left);
        this._wandGrater(walls.right);
    }

    _compileAussenwand(side, wall) {
        wall.box = this.corpusBox.clone();

        if (side === 'left') {
            _.first(this.elements).wallLeft = wall;
        } else {
            _.last(this.elements).wallRight = wall;
        }

        if (side === 'left') {
            wall.box.max.x = wall.box.min.x + APP.settings.getFloat('aussenwand_staerke');
        } else {
            wall.box.min.x = wall.box.max.x - APP.settings.getFloat('aussenwand_staerke');
        }

        if (!APP.settings.getBool('aussenwand_durchgehend_unten')) {
            wall.box.min.y = this.elementsBox.min.y;
        }

        if (side === 'left' && _.first(this.elements).locked.y) {
            wall.box.max.y = this.elementsBox.min.y + _.first(this.elements).size.y + APP.settings.getFloat('deckel_staerke');
        } else if (side === 'right' && _.last(this.elements).locked.y) {
            wall.box.max.y = this.elementsBox.min.y + _.last(this.elements).size.y + APP.settings.getFloat('deckel_staerke');
        }

        if (!this.deckel.needEinzelneDeckel && !APP.settings.getBool('aussenwand_durchgehend_oben',false)) {
            wall.box.max.y -= APP.settings.getFloat('deckel_staerke');
        }

        if (this.arrangement.isSchwebetuer() || this.arrangement.isShojituerAussen()) {
            const schiebetuer_vorsprung_min = APP.settings.getMinValue('schiebetuer_vorsprung', 2.5);
            const schiebetuer_vorsprung_max = APP.settings.getMaxValue('schiebetuer_vorsprung', 5);
            let offset = (schiebetuer_vorsprung_min - APP.settings.getFloat('schiebetuer_griffleiste_staerke', 0.3) - APP.settings.getFloat('schiebetuer_abstand_aussenwand', 0.5));

            if (side === (this.arrangement.schiebetuerLinksVorne ? 'left' : 'right') && this.elements.length % 2 === 0) {
                offset = (schiebetuer_vorsprung_min + schiebetuer_vorsprung_max - APP.settings.getFloat('schiebetuer_griffleiste_staerke', 0.3) - APP.settings.getFloat('schiebetuer_abstand_aussenwand', 0.5));
            }
            wall.box.max.z += offset;
        }

        wall.boxToVertices();
        wall.compile();
    }

    _compileInnenwaende() {
        const lazyWalls = APP.model.lazyPool(this, 'innerWalls', () => {
            const brett = new Brett(this, 'Seitenwand', 'yz');
            brett.isInner = true;
            return brett;
        });
        const numberOfInnenwaende = this.elements.length - 1;

        const innerWallBox = this.elementsBox.clone();
        innerWallBox.min.z = this.corpusBox.min.z;
        if (this.deckel.needEinzelneDeckel) {
            innerWallBox.max.y += APP.settings.getFloat('deckel_staerke');
        }
        innerWallBox.max.z -= APP.settings.getFloat('aussenwand_vorsprung');

        if (this.arrangement.isShojituerInnen()) {
            innerWallBox.max.z -= APP.settings.getFloat('shoji_innenwand_abstand_vorne',10);
        }

        for (let i = 0; i < numberOfInnenwaende; i++) {
            const element = this.elements[i];
            const nextElement = this.elements[i + 1];
            const wall = lazyWalls.next();
            wall.box = innerWallBox.clone();

            wall.box.min.x = element.box.max.x;
            wall.box.max.x = nextElement.box.min.x;

            if (element.locked.y || nextElement.locked.y) {
                wall.box.max.y = this.elementsBox.min.y +
                    Math.max(element.size.y, nextElement.size.y) +
                    APP.settings.getFloat('deckel_staerke');
            }

            element.wallRight = wall;
            nextElement.wallLeft = wall;
            this._wandGrater(wall);
        }
        lazyWalls.cleanup();
        this.innerWalls.forEach(wall => wall.compile());
    }

    _compileFrontCounts() {
        // clear
        this.innerWalls.forEach(x => x.frontCount = 0);
        this.outerWalls.left.frontCount = 0;
        this.outerWalls.right.frontCount = 0;
        this.deckel.boeden.forEach(x => x.frontCount = 0);
        this.schrankboden.frontCount = 0;
        this.elements.forEach(x => x.konstruktionsboeden.forEach(x => x.frontCount = 0));
        // count
        this.elements.forEach(element => element.compileFrontCounts());
        // Korrektur für Deckel / Boden
        this.deckel.boeden.forEach(x => x.frontCount = Math.min(1, x.frontCount));
        this.schrankboden.frontCount = Math.min(1, this.schrankboden.frontCount);

    }

    _compileFuesse() {
        const radius = APP.settings.getFloat('puck_fuesse_durchmesser') / 2;
        const height = APP.settings.getFloat('puck_fuesse_hoehe');
        const abstand = APP.settings.getFloat('puck_fuesse_abstand');
        const vorsprung = APP.settings.getFloat('aussenwand_vorsprung');

        const lazyFuesse = APP.model.lazyPool(this, 'fuesse', () => ({ position: v3() }), () => {
            const material = new THREE.MeshBasicMaterial({ color: APP.settings.getInt('puck_fuesse_farbe') });
            const geometry = new THREE.CylinderBufferGeometry(radius, radius, height, 32, 32);
            const cylinder = new THREE.Mesh(geometry, material);
            cylinder.vsUpdate = () => {
                cylinder.position.copy(cylinder.vsData.position);
            };
            this.object3d.add(cylinder);
            return cylinder;
        });

        if (APP.settings.getBool('puck_fuesse_anzeigen')) {
            const fuesse = _.range(4).map(x => lazyFuesse.next());
            const box = this.box.clone()
                .expandByVector(v3(-radius - abstand, 0, -radius - abstand - vorsprung));
            box.min.y = height / 2;
            fuesse[0].position.set(box.min.x, box.min.y, box.min.z);
            fuesse[1].position.set(box.min.x, box.min.y, box.max.z);
            fuesse[2].position.set(box.max.x, box.min.y, box.min.z);
            fuesse[3].position.set(box.max.x, box.min.y, box.max.z);
        }

        lazyFuesse.cleanup();
    }

    _checkSizes(initiator = null) {
        const element_breite_min = APP.settings.getMinValue('element_breite');
        const element_breite_max = APP.settings.getMaxValue('element_breite');

        const {minElementeAnzahl,maxElementeAnzahl} = this.getElementGrenzwerte();

        const check = (minmax: string, value: number, borderValue: number, title: string, message: string) => {
            if (minmax == 'min' && value < borderValue || minmax == 'max' && value > borderValue) {
                value = borderValue;
                APP.model.maybeThrowCompileError(this, title, message, borderValue);
            }
            return value;
        };

        let maxHeightI18nKey = 'maxHeightText';
        if (this.arrangement.isSchwebetuerOrShoji()) {
            maxHeightI18nKey = 'maxHeightTextAlternative';
        }

        this.size.x = check('min', this.size.x, APP.settings.getMinValue('schrank_breite'), 'minWidth', 'minWidthText');
        this.size.x = check('max', this.size.x, APP.settings.getMaxValue('schrank_breite'), 'maxWidth', 'maxWidthText');


        if (!APP.arrangement.isShojituer()) {
            this.size.x = check('min', this.size.x,
                (minElementeAnzahl * element_breite_min) + this.getSummeAllerBrettstaerkenKorpusBreite(minElementeAnzahl),
                'minWidth', 'minWidthText');

            this.size.x = check('max', this.size.x,
                (maxElementeAnzahl * element_breite_max) + this.getSummeAllerBrettstaerkenKorpusBreite(maxElementeAnzahl),
                'maxWidth', 'maxWidthText');

        }

        if (APP.settings.getValue('dachschraege') === 'links' || APP.settings.getValue('dachschraege') === 'rechts') {
            this.size.x = check('min', this.size.x,
                this.sizeTop.x + APP.settings.getMaxValue('breite_oben'),
                'minWidth', 'minWidthText');
        }

        this.size.y = check('min', this.size.y, APP.settings.getMinValue('schrank_hoehe'), 'minHeight', 'minHeightText');
        this.size.y = check('min', this.size.y, this.getInterieurMinMaxBoxWithoutTueren().max.y, 'minHeight', 'minHeightAusstattungText');
        this.size.y = check('max', this.size.y, APP.settings.getMaxValue('schrank_hoehe'), 'maxHeight', maxHeightI18nKey);

        if (APP.settings.getValue('dachschraege', null) !== null) {
            if (initiator === 'setDimensionTop') {
                this.size.y = check('min', this.size.y, this.size.y - APP.settings.getMinValue('kniestock'), 'minHeight', 'minHeightText');
            } else {
                this.size.y = check('min', this.size.y, this.sizeTop.y + APP.settings.getMinValue('kniestock'), 'minHeight', 'minHeightText');
            }
        }
        // JVI: Wird nicht gemacht,weil man mit der Schrankhöhe die Elemente ändern kann
        // if (this.elements.some(e => e.locked.y)) {
        //     let minY = 0;
        //     this.elements.forEach(e => minY = Math.max(minY, e.size.y));
        //     check('min',minY, 'minHeight', 'minHeightText');
        // }

        if (this.elements.every(e => e.locked.y)) {
            let maxY = 0;
            this.elements.forEach(e => maxY = Math.max(maxY, e.size.y + e.outerOffset));
            this.size.y = maxY;
        }

        const box = new THREE.Box3();
        box.min.z = APP.settings.getMinValue('schrank_tiefe');
        box.max.z = APP.settings.getMaxValue('schrank_tiefe');

        box.union(this.getInterieurMinMaxBoxWithoutTueren());
        box.min.z = Math.max(box.min.z, APP.settings.getMinValue('schrank_tiefe'));
        box.max.z = Math.min(box.max.z, APP.settings.getMaxValue('schrank_tiefe'));

        this.size.z = check('min', this.size.z, box.min.z, 'minDepth', 'minDepthText');
        this.size.z = check('max', this.size.z, box.max.z, 'maxDepth', 'maxDepthText');

        if (this.angleSide === 'left' || this.angleSide === 'right') {
            this.sizeTop.x = check('min', this.sizeTop.x, APP.settings.getMinValue('breite_oben'), 'minWidth', 'minWidthText');
            this.sizeTop.x = check('max', this.sizeTop.x, this.size.x - APP.settings.getMaxValue('breite_oben'), 'maxWidth', 'maxWidthText');
        }
        if (this.angleSide === 'left' || this.angleSide === 'right' || this.angleSide === 'back') {
            this.sizeTop.y = check('min', this.sizeTop.y, APP.settings.getMinValue('kniestock'), 'dialogSchrankKniestockMinHeader', 'dialogSchrankKniestockMinText');
            this.sizeTop.y = check('max', this.sizeTop.y, this.size.y - APP.settings.getMinValue('kniestock'), 'dialogSchrankKniestockMaxHeader', 'dialogSchrankKniestockMaxText');
        }
        if (this.angleSide === 'back') {
            this.size.z = check('min', this.size.z, this.sizeTop.z, 'maxDepth', 'maxDepthText');
            this.sizeTop.z = check('min', this.sizeTop.z, APP.settings.getMinValue('tiefe_oben'), 'minDepth', 'minDepthText');
            this.sizeTop.z = check('max', this.sizeTop.z, this.size.z - APP.settings.getMinValue('tiefe_oben'), 'maxDepth', 'maxDepthText');
        }
    }

    _wandGrater(wall) {
        wall.boxToVertices();
        if (this.angleSide === 'back') {
            wall.xEdgeZY(0, 1).z = wall.box.max.z - this.sizeTop.z;
            wall.xEdgeZY(2, 1)._ = { y: this.sizeTop.y, z: wall.box.min.z };
            wall.updateBox();
        } else if (this.slopings.isSloped(wall.box)) {
            wall.zEdgeXY(0, 1).y = this.slopings.yOfX(wall.box.min.x, 0);
            wall.zEdgeXY(1, 1).y = this.slopings.yOfX(wall.box.max.x, 0);
            wall.updateBox();
        }
    }

    _getFrontOffset() {
        let offset = 0;
        this.elements.forEach(element => element.interieurs.forEach(interieur => offset = Math.max(offset, interieur.getFrontOffset())));
        if (this.arrangement.isSchwebetuer() || this.arrangement.isShojituerAussen()) {
            offset = Math.max(offset, this.schwebetuer.getFrontOffset());
        }
        if (offset > 0) {
            offset += APP.settings.getFloat('korpus_extra_offset');
        }
        return offset;
    }

    setNumberOfElements(numOfElemente) {
        this._compileElementWidthAndNumber(numOfElemente);
    }

    setHeightToElementMaximum() {
        const elementMaximum = this.elements.reduce((c, e) => Math.max(c, e.getOuterHeight()), 0);
        this.setDimension("y", elementMaximum);
    }

    setDimension(dir: string, value: number) {
        this.iterateInterieur(FullHeightDragable.maybeSetFollowTopOfElement);
        this.size[dir] = value;
        this._checkSizes('setDimension');
    }

    setDimensionTop(dir: string, value: number) {
        this.iterateInterieur(FullHeightDragable.maybeSetFollowTopOfElement);
        this.sizeTop[dir] = value;
        this._checkSizes('setDimensionTop');
    }

    clearLocked() {
        this.elements.forEach(e => e.clearLocked());
    }

    getInterieurMinMaxBoxWithoutTueren() {
        const box = new THREE.Box3();
        this.elements.forEach((element) => box.union(element.getInterieurMinMaxBoxWithoutFullHeightDrag()));
        return box;
    }

    getBox3World(unionBox = new THREE.Box3()) {
        this.box.getCorners().forEach(v => {
            v.applyMatrix4(this.matrix);
            unionBox.expandByPoint(v);
        });
        return unionBox;
    }

    iterateElements(iterator) {
        this.elements.forEach(e => e.iterateElements(iterator));
    }

    iterateInterieur(iterator) {
        this.iterateElements(e => e.interieurs.forEach(x=>x.iterate(iterator)));
    }

    get child3dContainer() {
        return this.object3d;
    }

    get schrank() {
        return this;
    }

    schrankToLocal(v: THREE.Vector3) {
        return v.clone();
    }

    localToSchrank(v: THREE.Vector3) {
        return v.clone();
    }

    /**
     * ist false, falls dieses Objekt Teil des this.arrangement ist.
     */
    isOrphaned() {
        if (this.arrangement == null) {
            return true;
        }
        if (!this.arrangement.schraenke.includes(this)) {
            return true;
        }
        return this.arrangement.isOrphaned();
    }

    isChildOf(parent) {
        return this.arrangement === parent;
    }

    //--- Koordinatensystem

    initCoordinateSystem() {
        this._matrix = new THREE.Matrix4();
        this._inverseMatrix = new THREE.Matrix4();
        const setMatrixNeedsUpdate = () => this._matrixNeedsUpdate = true;
        this.position = THREE.Vector3.initWithObservation(setMatrixNeedsUpdate);
        this.quaternion = new THREE.Quaternion().onChange(setMatrixNeedsUpdate);
    }

    _maybeUpdateMatrix() {
        if (this._matrixNeedsUpdate) {
            this._matrix.compose(this.position, this.quaternion, Maths3.vector.one);
            this._inverseMatrix.copy(this._matrix).invert();
            this._matrixNeedsUpdate = false;
        }
    }

    get matrix(): THREE.Matrix4 {
        this._maybeUpdateMatrix();
        return this._matrix;
    }

    get inverseMatrix(): THREE.Matrix4 {
        this._maybeUpdateMatrix();
        return this._matrix;
    }

    localToWorld(vector: THREE.Vector3): THREE.Vector3 {
        return vector.applyMatrix4(this.matrix);
    }

    worldToLocal(vector: THREE.Vector3): THREE.Vector3 {
        return vector.applyMatrix4(this.inverseMatrix);
    }

    get rueckwandOuterZOffset() {
        return APP.settings.getFloat('rueckwand_vorsprung') - APP.settings.getFloat('rueckwand_dicke');
    }

    get rueckwandInnerZOffset() {
        return APP.settings.getFloat('rueckwand_vorsprung');
    }

    get isVerdeckt() {
        return this.verdeckungsIntervale.length > 0;
    }

    get verdeckteSchraenke() {
        const schraenke = [];
        this.arrangement.schraenke.forEach((schrank) => {
            schrank.elements.forEach((element) => {

                if (element.unverdeckt.linksVerdeckt || element.unverdeckt.rechtssVerdeckt) {

                }
            });
        });
        return schraenke;
    }

    /**
     * Gibt immer die Aussenmasse an. Bei verdeckten Eckschränken wird die Breite des Gesamtschrankes mit beiden
     * Flügeln zurückgegeben.
     * @returns {*}
     */
    get sizeOuter() {
        const arrangementSize = this.arrangement.getBox3World().getSize();
        const x = this.isVerdeckt ? this.size.x : (Math.abs(this.drehungAbsolut) < 45 ? arrangementSize.x : arrangementSize.z);
        const y = arrangementSize.y;
        const z = this.size.z + (this.sizeZisInnenmass ? this._getFrontOffset() : 0);
        return v3(x, y, z);
    }

    /**
     * gibt den absoluten Drehwinkel des Schrankes in Grad zurück. 0° = wie ein einzelner Standard-Schrank orientiert
     */
    get drehungAbsolut(): number {
        const z_ = v3(0, 0, 1).applyQuaternion(this.quaternion);
        return Maths3.angleClockwise(Maths3.vector.z, z_, Maths3.vector.y) * 180 / Math.PI;
    }

    // ---

    selectElement(elementNo) {
        this.elements.forEach(e => e.selected = false);
        if (elementNo >= 0) {
            this.elements[elementNo].selected = true;
        }
    }


    iterate(iterator) {
        iterator(this);
        this.elements.forEach(x=>x.iterate(iterator));
        this.innerWalls.forEach(x=>x.iterate(iterator));
        this.outerWalls.left && this.outerWalls.left.iterate(iterator);
        this.outerWalls.right && this.outerWalls.right.iterate(iterator);
        this.deckel && this.deckel.iterate(iterator);
        this.rueckwand && this.rueckwand.iterate(iterator);
        this.schrankboden && this.schrankboden.iterate(iterator);
        this.sockel && this.sockel.iterate(iterator);
        this.schwebetuer && this.schwebetuer.iterate(iterator);
        this.shojituer && this.shojituer.iterate(iterator);
    }

    getSchrankBreitenZuElementAnzahlIntervalle = Commons.cache(()=>{
        // sortiert die Reihenfolge um, so dass das 270 Intervall zuerst und das 0 zuletzt kommt, damit find <= suchen kann
        // const fallback = '[[0,2],[240,2,3],[270.01,3]]';
        const raw_value = APP.settings.getValue('schrankbreiten_zu_anzahl_elemente',null);
        if (raw_value==null) {
            return null;
        }
        return _.sortBy(JSON.parse(raw_value),i=>-i[0]);
    });


    getSummeAllerBrettstaerkenKorpusBreite(elementAnzahl) {
        return 2 * APP.settings.getFloat('aussenwand_staerke') + (elementAnzahl - 1) * APP.settings.getFloat('brett_staerke')
    }

    getAnzahlElementeZuVerteilenBeiGleichverteilung(width,element_breite,elementLockedAnzahl,elementLockedBreite) {
        const brett_staerke = APP.settings.getFloat('brett_staerke');
        const aussenwand_staerke = APP.settings.getFloat('aussenwand_staerke');
        const breiteZuVerteilen = width-elementLockedBreite-elementLockedAnzahl*brett_staerke-2*aussenwand_staerke;
        const anzahl = (breiteZuVerteilen+brett_staerke)/(element_breite+brett_staerke);
        return anzahl;
    }

    getFreieElementBreitenBeiGleichverteilung(width,element_anzahl,elementLockedAnzahl,elementLockedBreite) {
        const brett_staerke = APP.settings.getFloat('brett_staerke');
        const aussenwand_staerke = APP.settings.getFloat('aussenwand_staerke');
        const breiteZuVerteilen = width-elementLockedBreite-elementLockedAnzahl*brett_staerke-2*aussenwand_staerke;
        const freieElementBreiten = Maths.dividedEqually(breiteZuVerteilen+brett_staerke-element_anzahl*brett_staerke, element_anzahl, 2);
        return freieElementBreiten;
    }

    /**
     * wenn sollElementAnzahl gesetzt ist, dann wird eine Verteilung für diese Elementanzahl berechnet. Ansonsten wird
     * die vorhandene Anzahl Elemente als Grundlage benutzt. Die gegebene Breite des Schrankes hat sehr hohe Priorität
     * und wird nur geändert, wenn grundlegende Regeln verletzt würden.
     * @param sollElementAnzahl
     * @return {{schrankBreiteMin: *, schrankBreite: *, maxElementeAnzahl: number, schrankBreiteMax: *,
     *     minElementeAnzahl: number, freieElementBreiten: *, element_breite_max, element_breite_min,
     *     freieElementeAnzahl: number, elementeAnzahl: number}}
     */
    getElementGrenzwerte(sollElementAnzahl=undefined) {
        const decimals = 2;
        const const_min_anzahl = 1;
        const const_max_anzahl = 100;

        const element_breite_min = APP.settings.getMinValue('element_breite');
        const element_breite_max = APP.settings.getMaxValue('element_breite');

        const anzahl_elemente_min = APP.settings.getMinValue('anzahl_elemente', const_min_anzahl);
        const anzahl_elemente_max = APP.settings.getMaxValue('anzahl_elemente', const_max_anzahl);

        const schrank_breite_min = APP.settings.getMinValue('schrank_breite');
        const schrank_breite_max = APP.settings.getMaxValue('schrank_breite');

        this.elements = this.elements || [];

        let elementeAnzahl = sollElementAnzahl === undefined ? this.elements.length : sollElementAnzahl;

        const schrank_breite_min_from_elements =
            anzahl_elemente_min*element_breite_min+this.getSummeAllerBrettstaerkenKorpusBreite( anzahl_elemente_min );

        const schrank_breite_max_from_elements =
            anzahl_elemente_max*element_breite_max+this.getSummeAllerBrettstaerkenKorpusBreite( anzahl_elemente_max );

        const schrankBreiteMin = Maths.ceil(Math.max(schrank_breite_min,schrank_breite_min_from_elements),decimals);
        const schrankBreiteMax = Maths.floor(Math.min(schrank_breite_max,schrank_breite_max_from_elements),decimals);

        const schrankBreite = Maths.round(Maths.cutBetween(schrankBreiteMin,Maths.nanToZero(this.size.x),schrankBreiteMax),decimals);

        const intervalle = this.getSchrankBreitenZuElementAnzahlIntervalle();
        const elementAnzahlen = intervalle?.find(i => i[0] <= schrankBreite).slice(1);

        const elementAnzahlenZero = Commons.isEmpty(elementAnzahlen);

        const min_anzahl_bound = Math.max(const_min_anzahl, anzahl_elemente_min, elementAnzahlenZero ? 0 : _.min(elementAnzahlen));
        const max_anzahl_bound = Math.min(const_max_anzahl, anzahl_elemente_max, elementAnzahlenZero ? Number.MAX_VALUE : _.max(elementAnzahlen));

        let minElementeAnzahl = min_anzahl_bound;
        let maxElementeAnzahl = max_anzahl_bound;

        if (!APP.arrangement.isShojituer) {
            while (minElementeAnzahl<maxElementeAnzahl) {
                const changes = this.getElementChangesToSatisfy(minElementeAnzahl, schrankBreite);
                const {sollBreiteErreicht,sollAnzahlErreicht} = changes;
                if (sollBreiteErreicht && sollAnzahlErreicht) {
                    break;
                }
                minElementeAnzahl++;
            }

            while (minElementeAnzahl<maxElementeAnzahl) {
                const changes = this.getElementChangesToSatisfy(maxElementeAnzahl, schrankBreite);
                const {sollBreiteErreicht,sollAnzahlErreicht} = changes;
                if (sollBreiteErreicht && sollAnzahlErreicht) {
                    break;
                }
                maxElementeAnzahl--;
            }
        }

        elementeAnzahl = Maths.cutBetween(minElementeAnzahl,elementeAnzahl,maxElementeAnzahl);


        return {schrankBreite,elementeAnzahl,minElementeAnzahl,maxElementeAnzahl};
    }

    _compileElementWidthAndNumber(elementeAnzahlSoll=undefined) {
        const {schrankBreite,elementeAnzahl} = this.getElementGrenzwerte(elementeAnzahlSoll);
        this.elements = this.elements || [];
        // this.logii = true;
        const {changes} = this.getElementChangesToSatisfy(elementeAnzahl,schrankBreite);
        // this.logii = false;
        // console.log('_compileElementWidthAndNumber',schrankBreite,elementeAnzahl,changes);
        this.elements = changes.map(({element,neueBreite})=>{
            element.size.x = neueBreite;
            return element;
        });
    }

    getElementImportanceForDelete(element) {
        let importance = element.locked.x ? 3 : 0;
        importance += element.isVerdecktOderVerdecktSelbst() ? 2 : 0;
        importance += element.interieurs.length > 0 ? 1 : 0;
        // zuletzt hinzugefügte Elemente sind marginal weniger wichtig
        importance += 0.1/(element.id+1);
        return importance;
    }

    /**
     * Berechnet zu (elementeAnzahlSoll,schrankBreiteSoll) eine optimale Liste von Elementbreiten, ohne aber diese
     * Änderungen auch schon durchzuführen.
     * @param elementeAnzahlSoll
     * @param schrankBreiteSoll
     * @return {{created, element: *, neueBreite: *}[]|*[]}
     */
    getElementChangesToSatisfy(elementeAnzahlSoll, schrankBreiteSoll) {
        const epsilon = 0.001;
        const decimals = 2;
        const brett_staerke = APP.settings.getFloat('brett_staerke');
        const aussenwand_staerke = APP.settings.getFloat('aussenwand_staerke');
        const element_breite_min = APP.settings.getMinValue('element_breite');
        const element_breite_max = APP.settings.getMaxValue('element_breite');

        const createElementChangeObject = ({
                                      element,
                                      created = false,
                                  }) => {
            const box = element.getMinMax(true);
            let neueBreite, breiteMin, breiteMax = 0;
            if (element.locked.x) {
                neueBreite = breiteMin = breiteMax = Maths.cutBetween(box.min.x,element.size.x,box.max.x);
            } else {
                neueBreite = box.min.x;
                breiteMin = box.min.x;
                breiteMax = box.max.x;
            }
            return {
                element,
                breiteMax,
                breiteMin,
                neueBreite,
                created,
            };
        };

        // ohne deletes => entspricht immer wirklich existierenden Elementen
        const changes = this.elements.map((element) => createElementChangeObject({element}));

        let restlicheZuVerteilendeBreite = 0;

        // aus irgend einem Grund verliert der Chrome Debugger accessor und changes_ beim Brakepoint bei der Kurzschreibweise...
        // const berechneSchrankBreite = (accessor=(change)=>change.neueBreite,changes_=changes)=> {
        const berechneSchrankBreite = (accessor,changes_)=> {
            if (accessor===undefined) {
                accessor = (change)=>change.neueBreite;
            }
            if (changes_===undefined) {
                changes_ = changes;
            }
            return Maths.round(
                _.sumBy(changes_,accessor) + this.getSummeAllerBrettstaerkenKorpusBreite(changes_.length)
                , decimals);
        }

        const loescheElementeBisSollErreicht = () => {
            // lösche Elemente solange wie nötig, und zwar werden die Elemente zuerst gelöscht, die nicht so wichtig
            // sind, und das bevorzugt die zuletzt hinzugefügten (die mit niedriger id)
            while (
                berechneSchrankBreite(change => change.breiteMax, [{breiteMax: element_breite_max}].concat(changes)) >= schrankBreiteSoll
                && (
                    changes.length > elementeAnzahlSoll
                    || berechneSchrankBreite(change => change.breiteMin) > schrankBreiteSoll
                )) {
                const toDelete = _.minBy(changes, change => this.getElementImportanceForDelete(change.element));
                const index = changes.indexOf(toDelete);
                changes.splice(index, 1);
            }
        };

        const erzeugeElementeBisSollErreicht = () => {
            // füge Elemente hinzu solange wie nötig, und zwar bevorzugt ans rechte Ende
            while (
                    changes.length===0
                    ||
                    (
                        berechneSchrankBreite(change => change.breiteMin, [{breiteMin: element_breite_min}].concat(changes)) <= schrankBreiteSoll
                        && (
                            changes.length < elementeAnzahlSoll
                            || berechneSchrankBreite(change => change.breiteMax) < schrankBreiteSoll
                        )
                    )
                ) {
                let insertIndex = changes.length;
                // bei Eckschränken sollten Elemente nicht an der Ecke eingefügt werden, dann eventuell links einfügen
                // oder in der Mitte
                if (changes.length > 0) {
                    const letztesVerdeckt = _.last(changes).element.isVerdecktOderVerdecktSelbst();
                    const erstesVerdeckt = _.first(changes).element.isVerdecktOderVerdecktSelbst();
                    let index = changes.length;
                    if (letztesVerdeckt && erstesVerdeckt) {
                        index = Math.floor(changes.length / 2);
                    } else if (erstesVerdeckt) {
                        index = changes.length;
                    } else {
                        index = 0;
                    }
                }
                const change = createElementChangeObject({element: new Element(this), created: true});
                changes.splice(insertIndex, 0, change);
            }
        }

        const verteileBreiteSoDassAlleElementeMöglichstGleichBreitSind = () => {
            const epsilon1 = 0.0001;
            const uniqueEpsilon = (numbersAscending) => numbersAscending.reduce((acc,n)=>acc.length==0 || Math.abs(_.last(acc)-n)>epsilon1 ? acc.concat(n) : acc,[]);
            const grenzen = uniqueEpsilon(changes.flatMap(c=>[c.breiteMin,c.breiteMax]).sort((a,b)=>a-b));
            // const log = (...x)=>{
            //     if (this.logii) {
            //         console.log(...x,changes.map(c=>[c.breiteMin,c.neueBreite,c.breiteMax]),restlicheZuVerteilendeBreite);
            //     }
            // }
            const luft = c=>c.breiteMax-c.neueBreite;
            const addBreite = (change,breite)=>{
                const neueBreite =
                    Maths.round(
                        Maths.cutBetween(
                            change.breiteMin,
                            change.neueBreite + Math.min(restlicheZuVerteilendeBreite,breite),
                            change.breiteMax)
                    , decimals);
                const delta = Math.max(0, neueBreite-change.neueBreite);
                change.neueBreite += delta;
                restlicheZuVerteilendeBreite -= delta;
            }
            grenzen.reduce((min,max)=>{
                if (restlicheZuVerteilendeBreite>epsilon1) {
                    // fülle alle Elemente bis max auf
                    // durch den vorherigen lauf ist garantiert (min war da max): alle c.neueBreite >= min oder c.breiteMax < min
                    const changesToFillUp = changes.filter(c=>c.neueBreite<max && luft(c)>epsilon1);
                    if (changesToFillUp.length>0) {
                        // log('a',min,max);
                        const maximalZuVerteilendeBreite = Math.min( (max-min) * changesToFillUp.length, restlicheZuVerteilendeBreite);
                        const zuVerteilendeBreiteProElement = Maths.round( maximalZuVerteilendeBreite / changesToFillUp.length, decimals);
                        changesToFillUp.forEach(change=>addBreite(change,zuVerteilendeBreiteProElement));
                        // log('b',min,max);
                    }
                }
                return max;
            },grenzen[0]);
            // es kann sein, dass es noch ein epsilon zu verteilende Breite gibt - gib es dem, wo am meisten Platz ist
            if (restlicheZuVerteilendeBreite>epsilon1) {
                const maxChange = _.maxBy(changes,luft);
                addBreite(maxChange,restlicheZuVerteilendeBreite);
            }
        }

        const seitenwandknickRegel = () => {
            // Seitenwandknickregel: verschiebe, falls die Seitenwand im Knick liegt, diese genau an den Knick
            // diese Regel hat oberste Priorität und setzt evtl. Min/Max/Locked ausser Kraft
            if (this.angleSide === 'left' || this.angleSide === 'right') {
                let x = aussenwand_staerke;
                for (let i = 0; i < changes.length - 1; i++) {
                    const veraenderung = changes[i];
                    if (this.slopeX < x) {
                        continue;
                    }

                    const xMin = x;
                    const xMax = xMin + veraenderung.neueBreite;
                    x = xMax + brett_staerke;

                    if (xMax < this.slopeX && this.slopeX < xMax + brett_staerke) {
                        const xNew = this.angleSide === 'left' ? this.slopeX - brett_staerke : this.slopeX;
                        const delta = xNew - xMax;
                        veraenderung.neueBreite += delta;
                        changes[i + 1].neueBreite -= delta;
                    }
                }
            }
        }

        const rundeBreitenAufMM = ()=>{
            changes.forEach(change=>change.neueBreite=Maths.round(change.neueBreite,decimals));
        }

        const istSchrankBreiteErreicht = () => Math.abs(schrankBreiteSoll-berechneSchrankBreite()) < epsilon;

        // const checkChanges = () => {
        //     if (this.logii) {
        //         changes.forEach(c=>{
        //             const not = c.breiteMin>c.neueBreite+0.0001 || c.breiteMax+0.0001<c.neueBreite;
        //             if (not) {
        //                 console.log('** wird knallen:',c);
        //                 debugger;
        //             }
        //         })
        //     }
        // }
        //
        const erreicheElementAnzahlUndVerteileDieBreite = () => {
            loescheElementeBisSollErreicht();
            erzeugeElementeBisSollErreicht();

            restlicheZuVerteilendeBreite = schrankBreiteSoll - berechneSchrankBreite();

            if (!istSchrankBreiteErreicht()) {
                // console.log('schrankBreiteSoll',schrankBreiteSoll,'berechneSchrankBreite',berechneSchrankBreite());
                // console.log('a',changes.map(c=>c.neueBreite));
                verteileBreiteSoDassAlleElementeMöglichstGleichBreitSind();
                // checkChanges();
            }

            seitenwandknickRegel();
            // checkChanges();

            rundeBreitenAufMM();
            // checkChanges();
        };

        // Top-Level Logik ---

        erreicheElementAnzahlUndVerteileDieBreite();

        const result = {
            changes,
            sollBreiteErreicht: istSchrankBreiteErreicht(),
            sollAnzahlErreicht: changes.length===elementeAnzahlSoll,
            elementeAnzahl: changes.length,
            schrankBreite: berechneSchrankBreite(),
            elementeAnzahlSoll,
            schrankBreiteSoll
        };

        return result;
    }


    // _compileElementWidthAndNumber(sollElementeAnzahl=undefined) {
    //
    //     const {
    //         elementeAnzahl,
    //         schrankBreite,
    //         freieElementBreiten
    //     } = this.getElementGrenzwerte(sollElementeAnzahl);
    //
    //     this.size.x = schrankBreite;
    //
    //     // lösche Elemente solange wie nötig, und zwar werden die Elemente zuerst gelöscht, die nicht so wichtig sind
    //     while (elementeAnzahl<this.elements.length) {
    //         const toDeleteIndex = Commons.minIndexBy(this.elements,e=>{
    //             let importance = e.locked.x?2:0;
    //             importance+=e.isVerdecktOderVerdecktSelbst()?2:0;
    //             importance+=e.interieurs.length>0?1:0;
    //             return importance;
    //         });
    //         this.elements.splice(toDeleteIndex,1);
    //     }
    //
    //     // füge Elemente hinzu solange wie nötig, und zwar bevorzugt ans rechte Ende
    //     while (elementeAnzahl>this.elements.length) {
    //         let insertIndex = this.elements.length;
    //         // bei Eckschränken sollten Elemente nicht an der Ecke eingefügt werden, dann eventuell links einfügen
    //         // oder in der Mitte
    //         if (this.elements.length>0) {
    //             const letztesVerdeckt = _.last(this.elements).isVerdecktOderVerdecktSelbst();
    //             const erstesVerdeckt = _.first(this.elements).isVerdecktOderVerdecktSelbst();
    //             let index = this.elements.length;
    //             if (letztesVerdeckt && erstesVerdeckt) {
    //                 index = Math.floor(this.elements.length/2);
    //             } else if (erstesVerdeckt) {
    //                 index = this.elements.length;
    //             } else {
    //                 index = 0;
    //             }
    //         }
    //         const element = new Element(this);
    //         this.elements.splice(insertIndex,0,element);
    //     }
    //
    //     e.size.x = Maths.round(Maths.cutBetween(box.min.x,e.size.x,box.max.x),2);
    //
    //     this.elements.filter(e=>!e.locked.x).forEach((e,i)=>e.size.x=freieElementBreiten[i]);
    //
    //     this.elements.forEach(e=>{
    //         const box = e.getMinMax(true);
    //         e.size.x = Maths.round(Maths.cutBetween(box.min.x,e.size.x,box.max.x),2);
    //     });
    //
    //     this._checkForSeitenwandInKnick();
    //
    // }


    // _compileElementWidthAndNumber() {
    //     const brettSpaceFormula = () => 2 * APP.settings.getFloat('aussenwand_staerke') + (this.elements.length - 1) * APP.settings.getFloat('brett_staerke');
    //     let freeInnerSize = Helper.round(this.size.x - brettSpaceFormula());
    //     let elementSpreadCount = this.elements.length;
    //     let lockedInnerSize = 0;
    //     let elementId = 0;
    //     this.elements.forEach(element => {
    //         if (element.locked.x) {
    //             freeInnerSize -= element.size.x;
    //             lockedInnerSize += element.size.x;
    //             elementSpreadCount -= 1;
    //         }
    //         elementId++;
    //     });
    //     let elementVariableWidth = freeInnerSize / elementSpreadCount;
    //
    //     if (elementSpreadCount === 0) {
    //         this.size.x = Helper.round(lockedInnerSize + brettSpaceFormula());
    //     } else {
    //         const elementsOutOfMinMax = [];
    //         let tries = 100;
    //         do {
    //             elementsOutOfMinMax.length = 0;
    //             let leftInnerSize = freeInnerSize;
    //             let elementLeftCount = elementSpreadCount;
    //             this.elements.forEach(element => {
    //                 if (!element.locked.x) {
    //                     const minMax = element.getMinMax(false);
    //                     if (elementVariableWidth < minMax.min.x) {
    //                         leftInnerSize -= minMax.min.x
    //                         element.size.x = minMax.min.x
    //                         elementLeftCount -= 1;
    //                         elementsOutOfMinMax.push(element);
    //                     }
    //                     if (elementVariableWidth > minMax.max.x) {
    //                         leftInnerSize -= minMax.max.x
    //                         element.size.x = minMax.max.x
    //                         elementLeftCount -= 1;
    //                         elementsOutOfMinMax.push(element);
    //                     }
    //                 }
    //             });
    //             // Wird ggf. -/+Infinity - passt trotzdem
    //             elementVariableWidth = (leftInnerSize / elementLeftCount);
    //             if (APP.settings.isWithinMinMaxValue('element_breite', elementVariableWidth)) {
    //                 break;
    //             }
    //             if (elementVariableWidth < APP.settings.getMinValue('element_breite')) {
    //                 const element = _.last(this.elements);
    //                 this.elements.splice(-1, 1);
    //                 freeInnerSize += APP.settings.getFloat('brett_staerke');
    //                 elementSpreadCount--;
    //                 if (elementSpreadCount === 0) {
    //                     break;
    //                     //this.elements.splice(-1, 1);
    //                 }
    //             } else if (elementVariableWidth > APP.settings.getMaxValue('element_breite')) {
    //                 this.elements.push(new Element(this));
    //                 freeInnerSize -= APP.settings.getFloat('brett_staerke');
    //                 elementSpreadCount++;
    //                 elementId++;
    //             }
    //             elementVariableWidth = freeInnerSize / elementSpreadCount;
    //             tries--;
    //         } while (tries > 0);
    //         if (this.elements.length === 0) {
    //             elementVariableWidth = APP.settings.getMinValue('element_breite');
    //             this.elements.push(new Element(this));
    //         }
    //         let elementSize = 0;
    //         this.elements.forEach(element => {
    //             element.deckel = [];
    //             element.rueckwand = [];
    //             if (!element.locked.x && elementsOutOfMinMax.indexOf(element) == -1) {
    //                 element.size.x = elementVariableWidth;
    //             }
    //             elementSize += element.size.x;
    //         });
    //         this.size.x = elementSize + brettSpaceFormula();
    //         // FIXME das in die Schleife integrieren
    //         this._checkForSeitenwandInKnick();
    //     }
    //     // Das hier kann passieren, wenn alle Elemente gelocked sind und insgesamt zu breit
    //     if (this.size.x > APP.settings.getMaxValue('schrank_breite')) {
    //         APP.model.maybeThrowCompileError(this, 'maxWidth', 'maxWidthText', APP.settings.getMaxValue('schrank_breite'));
    //     }
    //
    // }


}
